import { createContext, ReactNode, useEffect, useState } from "react";
import { ApplicationLoading } from "./ApplicationLoading";
import { Healths, Sessions, Users } from "../api/api.gen";
import { ServerUnavailableError } from "../api/api_client";
import { ApiStatus } from "../models/ApiStatus.gen";
import { OmniUser } from "../models/OmniUser.gen";
import { UserLogin } from "../models/View.UserLogin.gen";
import { UserRoleClaim } from "../models/View.UserRoleClaim.gen";
import { useSession } from "../hooks/useSession";

export const authContext = createContext<AuthContext>({} as any);

export interface AuthContext {
    user: OmniUser | undefined;
    userClaims: UserRoleClaim[] | undefined;
    numberFormatter: Intl.NumberFormat;
    isAuthenticated: boolean;
    isLoading: boolean;
    isInMaintenanceMode: boolean;
    refreshMaintenanceMode: () => void;
    currentOrganizationId: string | undefined;
    setCurrentOrganizationId: (newOrgId: string) => void;
    reload: () => Promise<void>;
    logIn: (login: UserLogin) => Promise<void>;
    logOut: () => Promise<void>;
}

interface AuthContextProps {
    children: ReactNode;
}

export const AuthContextProvider = ({ children }: AuthContextProps) => {
    const [user, setUser] = useSession<OmniUser>("currentUser");
    const [versionInfo, setVersionInfo] = useSession<ApiStatus>("versionInfo");
    const [hasSessionLoaded, setHasSessionLoaded] = useState(false);
    const [organizationId, setOrganizationId] = useState<string>();
    const [userClaims, setUserClaims] = useState<UserRoleClaim[]>();
    const [isLoading, setIsLoading] = useState(false);
    const [numberFormatter] = useState(new Intl.NumberFormat("en-US")); //T\ODO: move this to backend USER and use everywhere

    const logIn = async (login: UserLogin) => {
        setIsLoading(true);
        await Sessions.login(login);
        return checkAuthState().then(() => setIsLoading(false));
    };

    const logOut = async () => {
        setIsLoading(true);
        await Sessions.logout();
        setUser(undefined);
        setIsLoading(false);
    };

    const setCurrentOrganizationId = async (newOrgId: string) => {
        //Update the server with the newID
        await Users.setLastViewedOrganizationId(newOrgId);
        setOrganizationId(newOrgId);

        //Refresh the user
        await checkAuthState();
        window.location.href = "/"; //don't stay on page viewing another orgs data.
    };
    const updateVersionInfo = async () => {
        setIsLoading(true);
        try {
            const vInfo = await Healths.getStatus();
            setVersionInfo(vInfo);
        } catch (err) {
            if (err instanceof ServerUnavailableError) {
                // The server is unreachable, so every diagnostic here is unknown rather than zero —
                // reporting 0 bytes resident or an epoch start time would read as fact.
                setVersionInfo({
                    branch: '', hasUncommittedChanges: false, inMaintenanceMode: true,
                    lastCommitSHA: '', version: '', versionDate: new Date(),
                    startedAtUtc: undefined, machineName: undefined, workingSetBytes: undefined,
                });
            }
        }
        setIsLoading(false);
    };

    const checkAuthState = async () => {
        var currentUser: OmniUser | undefined = undefined;
        var userRoleClaims: undefined | UserRoleClaim[] = undefined;
        try {
            currentUser = await Sessions.currentIdentity();
            userRoleClaims = await Users.getCurrentUserRoleClaims();
            setOrganizationId((prev) => {
                return currentUser?.lastViewedOrganizationId ?? prev;
            });
        } catch (e) {
            //This means we couldn't get a session for this user
        }
        setUser(currentUser);
        setUserClaims(userRoleClaims);
        setHasSessionLoaded(true);
    };

    useEffect(() => {
        updateVersionInfo();
    }, [user]);

    useEffect(() => {
        const eventName = "auth:checkAuthState";
        const mmEvent = "auth:maintenanceMode";
        document.addEventListener(eventName, checkAuthState);
        document.addEventListener(mmEvent, updateVersionInfo);
        return () => {
            document.removeEventListener(eventName, checkAuthState);
            document.removeEventListener(mmEvent, updateVersionInfo);
        };
    });

    const isAuthenticated = !!user;
    const context: AuthContext = { user, userClaims, isAuthenticated, reload: checkAuthState, isLoading, currentOrganizationId: organizationId, setCurrentOrganizationId, logIn, logOut, numberFormatter, isInMaintenanceMode: (versionInfo?.inMaintenanceMode ?? false), refreshMaintenanceMode: updateVersionInfo };

    useEffect(() => {
        checkAuthState();
    }, []); //eslint-disable-line react-hooks/exhaustive-deps

    const childrenToRender = hasSessionLoaded ? children : <ApplicationLoading />;

    return <authContext.Provider value={context}>{childrenToRender}</authContext.Provider>;
};
